import React, {useEffect, useRef, useState} from 'react';
import _ from 'underscore';

import {assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import ExistingWorklistFormGroup from 'worklists/ExistingWorklistFormGroup';
import useSelectedWorklistId from 'worklists/useSelectedWorklistId';
import {ValidateWorklistItems, Worklist, WorklistSummary, WorklistType} from 'worklists/WorklistConstants';
import {fetchWorklist} from 'worklists/WorklistDAO';

/**
 * Component for searching for a worklist. This component manages the data fetching (of the
 * worklist, not details about the entities or containers within), and will invoke your `onChange`
 * callback whenever something changes.
 */

export interface WorklistSelectorDetails {
  worklist: Worklist | null;
  badItemIds: Array<string>;
  isValidating: boolean;
  errorMessage: string | null;
  infoMessage: string | null;
}

interface WorklistSelectorProps {
  disabled?: boolean;
  onChange: (params: WorklistSelectorDetails) => void;
  worklistTypes: Array<WorklistType>;
  validateItems?: ValidateWorklistItems;
  label?: string;
}

// Used to convert between an array and a string (for dependency checking)
const STRING_SEPARATOR = '$~';

export default function WorklistSelector(props: WorklistSelectorProps): JSX.Element {
  const [selectedWorklistId, updateWorklistId] = useSelectedWorklistId();
  const [selectedWorklist, setSelectedWorklist] = useState<Worklist | null>(null);
  // Errors prevent selection at all. Infos are for things that don't
  // prevent selection, but that the user may want to know about this
  // worklist before selecting it.
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [isValidating, setValidating] = useState(false);
  // We store the list of bad item ids as a string, so that they can easily be equality-tested in hooks
  const [badItemIds, setBadItemIds] = useState<string>('');
  // We cache the handlers in refs so they are usable in the effect hooks below
  const onChangeHandler = useRef(props.onChange);
  const validateItems = useRef(props.validateItems);

  assertAndContinue(
    props.worklistTypes.length > 0,
    'You did not pass any worklistTypes for the user to choose from.'
  );

  const updateSelectedWorklist = (worklistSummary: WorklistSummary | null): void => {
    updateWorklistId(worklistSummary);
    setSelectedWorklist(null);
  };

  // This hook acts as a "component did update" handler, passing changes up to the caller.
  useEffect(() => {
    onChangeHandler.current({
      worklist: selectedWorklist,
      badItemIds: badItemIds.split(STRING_SEPARATOR).filter((el) => el.length > 0),
      isValidating,
      errorMessage: error,
      infoMessage: info,
    });
  }, [onChangeHandler, selectedWorklist, badItemIds, isValidating, error, info]);

  useEffect(() => {
    async function fetchItems(id: string): Promise<void> {
      const worklist = await fetchWorklist(id);

      if (validateItems.current) {
        // Only send unarchived and accessible items, since we will skip archived/inaccessible items.
        const itemIdsToValidate = _.difference(
          worklist.itemIds,
          worklist.archivedItemIds,
          worklist.inaccessibleItemIds
        );
        const {invalidItemIds, errorMessage} = await validateItems.current(itemIdsToValidate);
        // In some cases we may have an error but no specific items that caused it,
        // so just an error message is sufficient to prevent selection.
        if (errorMessage != null) {
          setBadItemIds(invalidItemIds.join(STRING_SEPARATOR));
          setError(errorMessage);
          setValidating(false);
          return;
        }
      }

      if (worklist.inaccessibleItemIds.length > 0 || worklist.archivedItemIds.length > 0) {
        const nArchived = worklist.archivedItemIds.filter(
          (itemId) => !worklist.inaccessibleItemIds.includes(itemId)
        ).length;
        const nNoAccess = worklist.inaccessibleItemIds.length;
        const archiveMessage = nArchived > 0 ? `${nArchived} archived ${pluralize(nArchived, 'item')}` : null;
        const noAccessMessage =
          nNoAccess > 0 ? `${nNoAccess} inaccessible ${pluralize(nNoAccess, 'item')}` : null;
        const combinedMessage = _.compact([archiveMessage, noAccessMessage]).join(' and ');

        const infoMessage =
          `This worklist contains ${combinedMessage}. ` +
          `${pluralize(nArchived + nNoAccess, 'This', 'These')} ${pluralize(
            nArchived + nNoAccess,
            'item'
          )} will be skipped.`;

        setInfo(infoMessage);
      }

      setSelectedWorklist(worklist);
      setValidating(false);
    }

    setError('');
    setInfo('');
    setBadItemIds('');

    if (selectedWorklistId) {
      setValidating(true);
      fetchItems(selectedWorklistId);
    }
  }, [validateItems, selectedWorklistId]);

  return (
    <ExistingWorklistFormGroup
      label={props.label ?? 'Search for a worklist'}
      onChange={updateSelectedWorklist}
      worklistTypes={props.worklistTypes}
      disabled={props.disabled}
    />
  );
}
